import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRightLeft,
  Activity,
  CheckCircle,
  Plus,
  RefreshCw,
  Clock,
  Wallet,
} from 'lucide-react';
import { useCpPortal } from '../../hooks/useCpPortal';
import { KpiMetricCard } from '../dashboard/KpiMetricCard';
import { CP_IN_PROGRESS_STATUSES } from './cpDashboardConstants';

interface SummaryData {
  totalRuns: number;
  activeRuns: number;
  totalDisbursed: number;
  failedPayments: number;
  pendingFundingCount?: number;
  pendingFundingAmountCents?: number;
  disbursementFundingMode?: 'prefunded_float' | 'deposit_per_batch';
  kycPendingCount?: number;
  kycPendingAmount?: number;
}

interface RunRow {
  id: number;
  run_reference: string;
  rail: string;
  total_amount: string;
  total_count: number;
  status: string;
  created_at: string;
}

const formatZAR = (n: number) =>
  new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', minimumFractionDigits: 2 }).format(n);

const formatCents = (c: number) => formatZAR(c / 100);

const relativeTime = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const STATUS_STYLES: Record<string, string> = {
  draft: 'bg-slate-50 text-slate-700 border-slate-200',
  pending_approval: 'bg-amber-50 text-amber-700 border-amber-200',
  pending_funding: 'bg-sky-50 text-sky-700 border-sky-200',
  funded: 'bg-teal-50 text-teal-700 border-teal-200',
  approved: 'bg-blue-50 text-blue-700 border-blue-200',
  submitted: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  processing: 'bg-purple-50 text-purple-700 border-purple-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  partial: 'bg-orange-50 text-orange-700 border-orange-200',
  failed: 'bg-red-50 text-red-700 border-red-200',
  cancelled: 'bg-gray-50 text-gray-500 border-gray-200',
};

function statusLabel(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export const ClientDashboardOverlay: React.FC = () => {
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [userStats, setUserStats] = useState<{ totalUsers: number; kycVerified: number } | null>(null);
  const [recentRuns, setRecentRuns] = useState<RunRow[]>([]);
  const [loading, setLoading] = useState(true);
  const { apiBase, authHeaders, cpPath, user } = useCpPortal();
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    setLoading(true);
    const headers = authHeaders();

    try {
      const [summaryRes, runsRes, usersRes] = await Promise.all([
        fetch(`${apiBase}/summary`, { headers }),
        fetch(`${apiBase}/runs?limit=5`, { headers }),
        fetch(`${apiBase}/attributed-users/summary`, { headers }),
      ]);

      const summaryData = await summaryRes.json();
      const runsData = await runsRes.json();
      const usersData = await usersRes.json();

      if (summaryData.success) setSummary(summaryData.data);
      if (runsData.success) setRecentRuns(runsData.data.runs || []);
      if (usersData.success) {
        setUserStats({
          totalUsers: usersData.data.userStats?.totalUsers ?? 0,
          kycVerified: usersData.data.userStats?.kycVerified ?? 0,
        });
      }
    } catch (err) {
      console.error('Dashboard fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, [apiBase, authHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const onFocus = () => { fetchData(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchData]);

  const showFundingTile = summary
    ? summary.disbursementFundingMode === 'deposit_per_batch' || (summary.pendingFundingCount || 0) > 0
    : false;

  if (loading && !summary) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-4 border-[var(--muted)] border-t-[var(--primary)]" />
          <p className="text-sm text-[var(--muted-foreground)]">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[var(--foreground)]">
            Welcome back, {user?.name || 'User'}
          </h2>
          <p className="text-sm text-[var(--muted-foreground)]">
            Disbursement overview
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={fetchData}
            className="flex min-h-[36px] items-center gap-2 rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 py-1.5 text-sm text-[var(--muted-foreground)] transition-colors hover:bg-[var(--muted)]"
          >
            <RefreshCw className="h-4 w-4" aria-hidden />
            Refresh
          </button>
          {(user?.role === 'maker' || user?.role === 'admin') && (
            <button
              type="button"
              onClick={() => navigate(cpPath('/upload'))}
              className="flex min-h-[36px] items-center gap-2 rounded-lg bg-[var(--primary)] px-4 py-1.5 text-sm font-medium text-[var(--primary-foreground)] transition-opacity hover:opacity-90"
            >
              <Plus className="h-4 w-4" aria-hidden />
              New Run
            </button>
          )}
        </div>
      </div>

      {userStats && userStats.totalUsers > 0 && (
        <button
          type="button"
          onClick={() => navigate(cpPath('/users'))}
          className="w-full text-left rounded-xl border border-[var(--border)] bg-[var(--card)] p-4 transition-colors hover:bg-[var(--muted)]/40"
        >
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-[var(--foreground)]">Registered wallet users</p>
              <p className="text-xs text-[var(--muted-foreground)] mt-0.5">
                {userStats.totalUsers} attributed user{userStats.totalUsers === 1 ? '' : 's'} · {userStats.kycVerified} KYC verified
              </p>
            </div>
            <span className="text-sm font-medium text-[var(--primary)]">View report</span>
          </div>
        </button>
      )}

      <button
        type="button"
        onClick={() => navigate(cpPath('/reports'))}
        className="w-full text-left rounded-xl border border-[var(--border)] bg-[var(--card)] p-4 transition-colors hover:bg-[var(--muted)]/40"
      >
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-[var(--foreground)]">Transaction mix report</p>
            <p className="text-xs text-[var(--muted-foreground)] mt-0.5">
              Payments and VAS activity of your registered users — filter by date and download as CSV, Excel or PDF
            </p>
          </div>
          <span className="text-sm font-medium text-[var(--primary)]">Open reports</span>
        </div>
      </button>

      <div className={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${showFundingTile ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}>
        <KpiMetricCard
          label="Total Runs"
          metrics={[{ value: String(summary?.totalRuns ?? 0) }]}
          icon={ArrowRightLeft}
          accent="blue"
          onClick={() => navigate(cpPath('/runs'))}
        />
        <KpiMetricCard
          label="In progress"
          metrics={[{ value: String(summary?.activeRuns ?? 0) }]}
          footnote={
            (summary?.activeRuns ?? 0) === 0
              ? 'No runs waiting on checker, funding, or bank.'
              : 'Awaiting checker, funding, or processing.'
          }
          icon={Activity}
          accent="orange"
          onClick={() => navigate(`${cpPath('/runs')}?status=${encodeURIComponent(CP_IN_PROGRESS_STATUSES)}`)}
        />
        {showFundingTile && (
          <KpiMetricCard
            label="Funding required"
            metrics={[{
              value: summary?.pendingFundingCount
                ? String(summary.pendingFundingCount)
                : '0',
              label: summary?.pendingFundingCount
                ? formatCents(summary.pendingFundingAmountCents || 0)
                : undefined,
            }]}
            footnote="Runs waiting for a matching bank deposit."
            icon={Wallet}
            accent="blue"
            onClick={() => navigate(`${cpPath('/runs')}?status=pending_funding`)}
          />
        )}
        <KpiMetricCard
          label="Total Disbursed"
          metrics={[{ value: summary ? formatZAR(summary.totalDisbursed) : formatZAR(0) }]}
          footnote="Wallet credits issued, including KYC-locked balances."
          icon={CheckCircle}
          accent="green"
          onClick={() => navigate(`${cpPath('/runs')}?status=completed`)}
        />
      </div>

      {(summary?.kycPendingCount || 0) > 0 && (
        <button
          type="button"
          onClick={() => navigate(cpPath('/users'))}
          className="w-full text-left rounded-xl border border-amber-200 bg-amber-50 p-4 transition-colors hover:bg-amber-100/60"
        >
          <p className="text-sm font-semibold text-amber-900">Credited — KYC required</p>
          <p className="mt-0.5 text-xs text-amber-800">
            {summary!.kycPendingCount} payment{(summary!.kycPendingCount || 0) === 1 ? '' : 's'}{' '}
            ({formatZAR(summary!.kycPendingAmount || 0)}) sitting in recipient wallets until KYC Tier 1.
            The run is complete; spend is locked for FICA.
          </p>
        </button>
      )}

      <div className="rounded-xl border border-[var(--border)] bg-[var(--card)]">
        <div className="flex items-center justify-between border-b border-[var(--border)] px-5 py-4">
          <h3 className="text-sm font-semibold text-[var(--foreground)]">Recent Runs</h3>
          <button
            type="button"
            onClick={() => navigate(cpPath('/runs'))}
            className="text-sm font-medium text-[var(--primary)] transition-colors hover:underline"
          >
            View all
          </button>
        </div>

        {recentRuns.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Clock className="mb-3 h-10 w-10 text-[var(--muted-foreground)]/40" aria-hidden />
            <p className="text-sm text-[var(--muted-foreground)]">No disbursement runs yet</p>
            <p className="mt-1 text-xs text-[var(--muted-foreground)]">
              Upload a beneficiary file to create your first run
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-[var(--border)] bg-[var(--muted)]/30">
                  <th className="px-5 py-3 font-medium text-[var(--muted-foreground)]">Reference</th>
                  <th className="px-5 py-3 font-medium text-[var(--muted-foreground)]">Type</th>
                  <th className="px-5 py-3 font-medium text-[var(--muted-foreground)] text-right">Amount</th>
                  <th className="px-5 py-3 font-medium text-[var(--muted-foreground)] text-right">Count</th>
                  <th className="px-5 py-3 font-medium text-[var(--muted-foreground)]">Status</th>
                  <th className="px-5 py-3 font-medium text-[var(--muted-foreground)]">Created</th>
                </tr>
              </thead>
              <tbody>
                {recentRuns.map((run) => (
                  <tr
                    key={run.id}
                    onClick={() => navigate(cpPath(`/runs/${run.id}`))}
                    className="cursor-pointer border-b border-[var(--border)] last:border-0 transition-colors hover:bg-[var(--muted)]/30"
                  >
                    <td className="px-5 py-3 font-medium text-[var(--foreground)]">
                      {run.run_reference}
                    </td>
                    <td className="px-5 py-3 text-[var(--muted-foreground)]">Wallet</td>
                    <td className="px-5 py-3 text-right font-mono text-[var(--foreground)]">
                      {formatZAR(parseFloat(run.total_amount || '0'))}
                    </td>
                    <td className="px-5 py-3 text-right text-[var(--muted-foreground)]">
                      {run.total_count}
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                          STATUS_STYLES[run.status] || 'bg-gray-50 text-gray-600 border-gray-200'
                        }`}
                      >
                        {statusLabel(run.status)}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-xs text-[var(--muted-foreground)]">
                      {relativeTime(run.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
